/**
 * Tek, paylaşılan access-token yenileme noktası.
 *
 * Önceden App.js'in axios interceptor'ı ve messenger.js'in WebSocket
 * yeniden-bağlanma mantığı birbirinden habersiz İKİ AYRI /auth/refresh
 * çağrısı yapıyordu. Backend refresh token'ı rotate ettiği (tek kullanımlık,
 * bkz. backend/routes/auth_refresh.py) için eşzamanlı iki çağrıdan biri
 * "iptal edilmiş" hatası alıyor ve bunu çağıran taraf oturumu tamamen
 * siliyordu — oturum aslında hâlâ geçerliyken kullanıcı dışarı atılıyordu.
 *
 * Artık:
 *  - Aynı sekme içindeki tüm çağıranlar (axios interceptor, WS reconnect)
 *    bu tek fonksiyonu kullanır → _refreshPromise ile tek ağ isteğine iner.
 *  - Farklı sekmeler arası yarışı da azaltmak için (aynı origin'deki diğer
 *    sekmeler de aynı localStorage'ı paylaşır) navigator.locks destekleniyorsa
 *    kullanılır — kilit alındığında önce localStorage tekrar kontrol edilir,
 *    başka bir sekme zaten yenilemişse ağ isteği hiç atılmaz.
 *  - Yenileme isteği "iptal edilmiş" hatasıyla reddedilirse HEMEN oturum
 *    silinmez: localStorage'daki refresh_token gönderdiğimizden FARKLIYSA
 *    başka bir yol/sekme zaten yenilemiş demektir — o taze access token
 *    kullanılır. Sadece token değişmemişse ve yine de reddediliyorsa
 *    gerçekten geçersiz kabul edilip oturum temizlenir.
 */
import axios from "axios";
import { API } from "./api";
import { clearSession } from "./auth";

const REFRESH_LOCK_NAME = "app-token-refresh";

function syncAppSessionTokens(token, refreshToken) {
  try {
    const raw = localStorage.getItem("app_session");
    if (!raw) return;
    const sess = JSON.parse(raw);
    sess.token = token;
    if (refreshToken) sess.refresh_token = refreshToken;
    localStorage.setItem("app_session", JSON.stringify(sess));
  } catch (_) { /* noop */ }
}

async function _doRefresh(rt) {
  try {
    const resp = await axios.post(`${API}/auth/refresh`, { refresh_token: rt }, {
      // Bu istek interceptor'ın 401 yakalama döngüsüne girmesin (App.js)
      __skipAuthRefresh: true,
      headers: { "Content-Type": "application/json" },
    });
    if (resp?.data?.token) {
      localStorage.setItem("auth_token", resp.data.token);
      if (resp.data.refresh_token) {
        localStorage.setItem("refresh_token", resp.data.refresh_token);
      }
      syncAppSessionTokens(resp.data.token, resp.data.refresh_token);
      // Oturumu izleyenler (duyuru akışı vb.) yeni token'la hemen yenilesin
      try { window.dispatchEvent(new CustomEvent("auth-changed", { detail: { type: "refresh" } })); } catch (_) { /* noop */ }
      return resp.data.token;
    }
    return null;
  } catch (e) {
    // Ağ hatası / sunucu hatası (yanıt yok veya 5xx) → token'ın geçersiz olduğu
    // KANITLANMADI; oturumu silme, sadece bu denemeyi başarısız say.
    // 429 (hız sınırı) ve diğer 4xx'ler de kanıt değil — sadece 401 "reddedildi" demek.
    const status = e?.response?.status;
    if (status !== 401) return null;
    // Reddedildi — ama bu SIRADA başka bir sekme/yol zaten yenilemiş olabilir.
    const currentRt = localStorage.getItem("refresh_token");
    if (currentRt && currentRt !== rt) {
      const freshToken = localStorage.getItem("auth_token");
      if (freshToken) return freshToken;
    }
    // Token gerçekten değişmemiş (kimse yenilememiş) ve yine de reddedildi → geçersiz.
    clearSession();
    return null;
  }
}

let _refreshPromise = null;

export async function tryRefreshAccessToken() {
  if (_refreshPromise) return _refreshPromise;
  const rt = localStorage.getItem("refresh_token");
  if (!rt) return null;

  _refreshPromise = (async () => {
    try {
      if (typeof navigator !== "undefined" && navigator.locks?.request) {
        return await navigator.locks.request(REFRESH_LOCK_NAME, async () => {
          // Kilidi beklerken başka bir sekme zaten yenilemiş olabilir —
          // gereksiz ikinci ağ isteğinden kaçın.
          const latestRt = localStorage.getItem("refresh_token");
          if (latestRt && latestRt !== rt) {
            const freshToken = localStorage.getItem("auth_token");
            if (freshToken) return freshToken;
          }
          return _doRefresh(rt);
        });
      }
      return await _doRefresh(rt);
    } finally {
      _refreshPromise = null;
    }
  })();
  return _refreshPromise;
}
